import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Label, useToast } from '@caruso/ui';
import { useAuthStore } from '@/stores/auth-store';
import { usersService, UpdateUserDto } from '@/services/users.service';
import {
  User,
  Mail,
  Lock,
  Save,
  Calendar,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@caruso/ui';
import { api } from '@/lib/axios';

export function ProfilePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'calendar'>('profile');

  const [profileData, setProfileData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [calendarData, setCalendarData] = useState({
    googleAccessToken: '',
    googleRefreshToken: '',
    appleCalendarUrl: '',
    appleUsername: '',
    applePassword: '',
  });

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', currentUser?.id],
    queryFn: () => usersService.getById(currentUser!.id),
    enabled: !!currentUser?.id,
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateUserDto) => usersService.update(currentUser!.id, data),
    onSuccess: () => {
      toast({
        title: 'Perfil atualizado',
        description: 'Suas informações foram atualizadas com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['user', currentUser?.id] });
      // Limpar senhas
      setProfileData({
        ...profileData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar perfil',
        description: error.response?.data?.message || 'Não foi possível atualizar o perfil',
        variant: 'destructive',
      });
    },
  });

  const enableCalendarMutation = useMutation({
    mutationFn: (hasCalendar: boolean) =>
      usersService.update(currentUser!.id, { hasCalendar }),
    onSuccess: () => {
      toast({
        title: 'Configuração atualizada',
        description: 'Funcionalidade de agenda atualizada com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['user', currentUser?.id] });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar',
        description: error.response?.data?.message || 'Não foi possível atualizar',
        variant: 'destructive',
      });
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!profileData.name.trim()) {
      toast({
        title: 'Nome obrigatório',
        description: 'Por favor, informe seu nome',
        variant: 'destructive',
      });
      return;
    }

    if (!profileData.email.trim()) {
      toast({
        title: 'Email obrigatório',
        description: 'Por favor, informe seu email',
        variant: 'destructive',
      });
      return;
    }

    const updateData: UpdateUserDto = {
      name: profileData.name,
      email: profileData.email,
    };

    // Se está tentando alterar senha
    if (profileData.newPassword || profileData.confirmPassword) {
      if (profileData.newPassword !== profileData.confirmPassword) {
        toast({
          title: 'Senhas não conferem',
          description: 'A nova senha e a confirmação devem ser iguais',
          variant: 'destructive',
        });
        return;
      }

      if (profileData.newPassword.length < 6) {
        toast({
          title: 'Senha muito curta',
          description: 'A senha deve ter no mínimo 6 caracteres',
          variant: 'destructive',
        });
        return;
      }

      updateData.password = profileData.newPassword;
    }

    updateProfileMutation.mutate(updateData);
  };

  const handleGoogleConnect = async () => {
    try {
      // Aqui você implementaria o fluxo OAuth2 do Google
      toast({
        title: 'Em desenvolvimento',
        description: 'A integração com Google Calendar está em desenvolvimento',
        variant: 'default',
      });
    } catch (error) {
      toast({
        title: 'Erro ao conectar',
        description: 'Não foi possível conectar com o Google Calendar',
        variant: 'destructive',
      });
    }
  };

  const handleAppleConnect = async () => {
    try {
      if (!calendarData.appleCalendarUrl || !calendarData.appleUsername || !calendarData.applePassword) {
        toast({
          title: 'Dados incompletos',
          description: 'Por favor, preencha todos os campos do Apple Calendar',
          variant: 'destructive',
        });
        return;
      }

      // Salvar credenciais do Apple Calendar
      await usersService.update(currentUser!.id, {
        // Aqui você enviaria as credenciais criptografadas
      });

      toast({
        title: 'Conectado com sucesso',
        description: 'Apple Calendar conectado',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro ao conectar',
        description: 'Não foi possível conectar com o Apple Calendar',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-brand-smoke rounded w-48"></div>
          <div className="h-96 bg-brand-smoke rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-white">Meu Perfil</h1>
        <p className="text-brand-ivory/80 mt-2 text-lg">
          Gerencie suas informações pessoais e configurações
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-brand-gold/20">
        <button
          onClick={() => setActiveTab('profile')}
          className={cn(
            'px-6 py-3 font-semibold text-base transition-all relative',
            activeTab === 'profile'
              ? 'text-brand-gold'
              : 'text-brand-ivory/60 hover:text-brand-gold',
          )}
        >
          <div className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Dados Pessoais
          </div>
          {activeTab === 'profile' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-gold"></div>
          )}
        </button>
        {user?.hasCalendar && (
          <button
            onClick={() => setActiveTab('calendar')}
            className={cn(
              'px-6 py-3 font-semibold text-base transition-all relative',
              activeTab === 'calendar'
                ? 'text-brand-gold'
                : 'text-brand-ivory/60 hover:text-brand-gold',
            )}
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Integração de Agenda
            </div>
            {activeTab === 'calendar' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-gold"></div>
            )}
          </button>
        )}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <form onSubmit={handleProfileSubmit} className="space-y-6">
          <Card className="p-8 bg-brand-smoke border-brand-gold/20">
            <h2 className="text-2xl font-bold text-white mb-6">Informações Pessoais</h2>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white font-semibold">
                  Nome Completo
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
                  <Input
                    id="name"
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    placeholder="Seu nome completo"
                    className="pl-11 text-base"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white font-semibold">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    placeholder="seu@email.com"
                    className="pl-11 text-base"
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-8 bg-brand-smoke border-brand-gold/20">
            <h2 className="text-2xl font-bold text-white mb-2">Alterar Senha</h2>
            <p className="text-brand-ivory/70 mb-6">Deixe em branco se não quiser alterar</p>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-white font-semibold">
                  Nova Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
                  <Input
                    id="newPassword"
                    type="password"
                    value={profileData.newPassword}
                    onChange={(e) =>
                      setProfileData({ ...profileData, newPassword: e.target.value })
                    }
                    placeholder="Digite sua nova senha"
                    className="pl-11 text-base"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white font-semibold">
                  Confirmar Nova Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={profileData.confirmPassword}
                    onChange={(e) =>
                      setProfileData({ ...profileData, confirmPassword: e.target.value })
                    }
                    placeholder="Confirme sua nova senha"
                    className="pl-11 text-base"
                  />
                </div>
              </div>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={updateProfileMutation.isPending}
              className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90 font-semibold shadow-lg"
            >
              <Save className="w-5 h-5 mr-2" />
              {updateProfileMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-6">
          {/* Calendar Status */}
          <Card className="p-8 bg-brand-smoke border-brand-gold/20">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Status da Agenda</h2>
                <p className="text-brand-ivory/70">
                  {user?.hasCalendar
                    ? 'Sua agenda está habilitada e pronta para uso'
                    : 'Habilite a funcionalidade de agenda para começar'}
                </p>
              </div>
              <div
                className={cn(
                  'px-4 py-2 rounded-full font-semibold flex items-center gap-2',
                  user?.hasCalendar
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400',
                )}
              >
                {user?.hasCalendar ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Habilitada
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5" />
                    Desabilitada
                  </>
                )}
              </div>
            </div>

            <Button
              onClick={() => enableCalendarMutation.mutate(!user?.hasCalendar)}
              disabled={enableCalendarMutation.isPending}
              variant="outline"
              className={cn(
                'border-2',
                user?.hasCalendar
                  ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                  : 'border-green-500/30 text-green-400 hover:bg-green-500/10',
              )}
            >
              {user?.hasCalendar ? 'Desabilitar Agenda' : 'Habilitar Agenda'}
            </Button>
          </Card>

          {user?.hasCalendar && (
            <>
              {/* Google Calendar Integration */}
              <Card className="p-8 bg-brand-smoke border-brand-gold/20">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">Google Calendar</h3>
                    <p className="text-brand-ivory/70 mb-4">
                      Sincronize seus eventos automaticamente com o Google Calendar. Os eventos
                      criados aqui aparecerão no seu calendário do Google e vice-versa.
                    </p>

                    {user?.googleCalendarId ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="text-green-400 font-semibold">
                            Conectado e sincronizando
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          Desconectar Google Calendar
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={handleGoogleConnect}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                      >
                        <ExternalLink className="w-5 h-5 mr-2" />
                        Conectar com Google Calendar
                      </Button>
                    )}
                  </div>
                </div>
              </Card>

              {/* Apple Calendar Integration */}
              <Card className="p-8 bg-brand-smoke border-brand-gold/20">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">Apple Calendar (iCloud)</h3>
                    <p className="text-brand-ivory/70 mb-4">
                      Sincronize com o Apple Calendar usando CalDAV. Você precisará fornecer as
                      credenciais do iCloud.
                    </p>

                    {user?.appleCalendarId ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="text-green-400 font-semibold">
                            Conectado e sincronizando
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          Desconectar Apple Calendar
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-white font-semibold">URL CalDAV</Label>
                            <Input
                              type="url"
                              value={calendarData.appleCalendarUrl}
                              onChange={(e) =>
                                setCalendarData({ ...calendarData, appleCalendarUrl: e.target.value })
                              }
                              placeholder="https://caldav.icloud.com/..."
                              className="text-base"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-white font-semibold">Usuário iCloud</Label>
                              <Input
                                type="text"
                                value={calendarData.appleUsername}
                                onChange={(e) =>
                                  setCalendarData({ ...calendarData, appleUsername: e.target.value })
                                }
                                placeholder="seu@icloud.com"
                                className="text-base"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-white font-semibold">
                                Senha específica do app
                              </Label>
                              <Input
                                type="password"
                                value={calendarData.applePassword}
                                onChange={(e) =>
                                  setCalendarData({ ...calendarData, applePassword: e.target.value })
                                }
                                placeholder="••••-••••-••••-••••"
                                className="text-base"
                              />
                            </div>
                          </div>

                          <div className="flex items-start gap-3 px-4 py-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-400">
                              <p className="font-semibold mb-1">
                                Como obter senha específica do app:
                              </p>
                              <ol className="list-decimal list-inside space-y-1 text-blue-300">
                                <li>Acesse appleid.apple.com</li>
                                <li>Entre em "Segurança" → "Senhas específicas de apps"</li>
                                <li>Gere uma nova senha para "CRM"</li>
                                <li>Use a senha gerada aqui</li>
                              </ol>
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={handleAppleConnect}
                          className="bg-gray-800 hover:bg-gray-900 text-white font-semibold"
                        >
                          <ExternalLink className="w-5 h-5 mr-2" />
                          Conectar com Apple Calendar
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              {/* Sync Info */}
              <Card className="p-6 bg-brand-smoke border-brand-gold/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-brand-ivory/70">
                    <p className="font-semibold text-white mb-2">Sobre a sincronização:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>A sincronização é bidirecional - mudanças em qualquer lado são refletidas</li>
                      <li>Google Calendar sincroniza em tempo real via webhooks</li>
                      <li>Apple Calendar sincroniza a cada 5 minutos</li>
                      <li>Eventos excluídos em um calendário serão removidos do outro</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
